import {Badge, Button, Card, Spinner} from 'react-bootstrap'
import {FormattedMessage, useIntl} from 'react-intl'
import {Draggable, DroppableProvided} from 'react-beautiful-dnd'
import {Dispatch, SetStateAction, useCallback, useEffect, useRef, useState} from 'react'
import {Tooltip} from 'react-tooltip'
import {AllLeadStageEdge, IEdgesStagePipe, IInfoLead, IResAllLeadStage} from '../leadTypes'
import {useLazyQuery} from '@apollo/client'
import {GET_ALL_LEADS_BY_STAGE} from '../../../gql/queries/leadsQuery'
import {toast} from 'react-toastify'
import {parseId, toTitleCase} from '../../../helpers'
import Lottie from 'lottie-react'
import noLeads from '../../../assets/lotties/no-leads.json'
import moment from 'moment'
import {useDisclourse} from '../../../hooks/useDisclourse'
import {ModalSumCall} from '../../oneLead/components/ModalSumCall'
import {ModalDeadLead} from '../../oneLead/components/ModalDeadLead'
import {getClassTraffic} from '../LeadsPage'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {AssignmentGroupBadge} from './AssignmentGroupBadge'
import {LeadSourceBadge} from './LeadSourceBadge'
import {kz} from './kanbanZoom'

const FIRST = 20
const tenPlus = '10+'
const dash = '-'
const colon = ':'
const space = ' '
const slash = '/'
const initialSep = '. -'

const getTagFontSize = (text: string) => {
  const length = text?.length || 0
  if (length > 30) return kz(8)
  if (length > 20) return kz(9)
  if (length > 12) return kz(10)
  return kz(12)
}

const getItemStyle = (isDragging: boolean, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: 'none',
  padding: kz(8 * 2),
  margin: `0 0 ${kz(8)} 0`,

  // change background colour if dragging
  background: isDragging ? '#71C8F5' : 'transparent',

  // styles we need to apply on draggables
  ...draggableStyle,
})

interface IBodyLeadsProps {
  providedParent: DroppableProvided
  setInfoLead: Dispatch<SetStateAction<IInfoLead | null>>
  onOpenEdit: () => void
  updateOneLead: ({idStage, leads}: {idStage: string; leads: AllLeadStageEdge[]}) => void
  initTotal: ({idStage, total}: {idStage: string; total: number}) => void
  updateListLeads: () => void
  registerRefresh?: (stageId: string, fn: () => void) => void
  stageId: string
  stageName: string
  stageType?: string | null
  isLoadingBoard?: boolean
  boardFetchKey?: number
  autoLoad?: boolean
  flagLeads: number
  leads: AllLeadStageEdge[]
  total: number | null
  permissions: string[]
  valueUsers?: string[] | null
  filterUnassigned?: boolean
  datesFrom?: Date | null
  datesTo?: Date | null
  searchName?: string
  searchPhone?: string
  tagIds?: string[] | null
  isRingIntegration: boolean
  declinedStage?: IEdgesStagePipe | null
  leadsSelected?: {
    id: string
    name: string
    stage: string
    user: string
  }[]
  setLeadsSelected: (
    leads: {
      id: string
      name: string
      stage: string
      user: string
    }[]
  ) => void
}

export const BodyLeadsDragDrop: React.FC<IBodyLeadsProps> = ({
  providedParent,
  onOpenEdit,
  setInfoLead,
  updateOneLead,
  initTotal,
  updateListLeads,
  registerRefresh,
  stageId,
  stageName,
  stageType,
  isLoadingBoard = false,
  boardFetchKey = 0,
  autoLoad = false,
  flagLeads,
  leads,
  total,
  permissions,
  datesFrom,
  datesTo,
  searchName,
  searchPhone,
  tagIds,
  valueUsers,
  filterUnassigned,
  isRingIntegration,
  declinedStage,
  leadsSelected,
  setLeadsSelected,
}) => {
  const intl = useIntl()
  const [gqlGetLeads, {data, loading, error: errorTotal, refetch}] = useLazyQuery<IResAllLeadStage>(
    GET_ALL_LEADS_BY_STAGE,
    {fetchPolicy: 'no-cache'}
  )
  const hasFetchedRef = useRef(false)
  const [localShouldLoad, setLocalShouldLoad] = useState(autoLoad)

  // Cuando el board refetch (filtros/pipeline cambian), resetear al estado inicial
  useEffect(() => {
    setOffset(0)
    setLocalShouldLoad(false)
    hasFetchedRef.current = false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boardFetchKey])

  const [offset, setOffset] = useState(0)
  const requestLeads = useCallback(
    (nextOffset = offset) => {
      const variables = {
        stagePipelineId: parseId(stageId),
        first: FIRST,
        offset: nextOffset,
        userIds:
          valueUsers && valueUsers.length > 0 ? valueUsers.map((u) => Number(parseId(u))) : null,
        unassigned: filterUnassigned || null,
        tagIds: tagIds && tagIds.length > 0 ? tagIds.map((t) => Number(parseId(t))) : null,
        name_Icontains: searchName,
        phone_Icontains: searchPhone,
        fromDate: datesFrom != null ? moment(datesFrom).format('YYYY-MM-DD') : null,
        toDate: datesTo != null ? moment(datesTo).format('YYYY-MM-DD') : null,
      }
      if (!hasFetchedRef.current) {
        hasFetchedRef.current = true
        gqlGetLeads({variables})
        return
      }
      refetch(variables)
    },
    [
      offset,
      stageId,
      valueUsers,
      filterUnassigned,
      searchName,
      searchPhone,
      tagIds,
      datesFrom,
      datesTo,
      gqlGetLeads,
      refetch,
    ]
  )

  // Refresco de ESTA columna. Nunca se debe pasar el `refetch` crudo de Apollo a un
  // hijo: en modo board la lazy query de la columna todavía no se ejecutó (autoLoad
  // es false, los leads llegan por props del board), y `refetch` es un EAGER_METHOD:
  // dispara la query aunque nunca se haya ejecutado, usando solo las opciones del
  // hook — que no tienen variables. Resultado: `allLeads` sin stage, sin filtros y
  // sin `first`, y esa lista se vuelca en la columna. Siempre pasar esto.
  const refreshStage = useCallback(() => {
    requestLeads(offset)
  }, [requestLeads, offset])

  // Register a per-stage refresh function so parent can refetch only this stage
  useEffect(() => {
    if (!registerRefresh) return
    registerRefresh(String(parseId(stageId)), refreshStage)
    // No cleanup needed; parent can override by re-registering on change
  }, [registerRefresh, stageId, refreshStage])

  const {isOpen: isOpenCall, onClose: onCloseCall, onOpen: onOpenCall} = useDisclourse()
  const {isOpen: isOpenDeadLead, onClose: onCloseDeadLead, onOpen: onOpenDeadLead} = useDisclourse()

  const [idLead, setIdLead] = useState<null | string>(null)
  const [phone, setPhone] = useState<null | string>(null)
  const [infoLeadDead, setInfoLeadDead] = useState({
    idDeadLead: '',
    idNameLead: '',
  })
  const selectedLeads = leadsSelected ?? []
  const selectableLeadEdges = leads.filter(({node}) => !node.dialQueueLocked)
  const isAllSelected =
    selectableLeadEdges.length > 0 &&
    selectableLeadEdges.every(({node}) => selectedLeads.some((item) => item.id === node.id))

  const buildDialQueueLockLabel = useCallback(
    (owner?: string | null) => {
      const normalizedOwner = String(owner || '').trim()
      return normalizedOwner
        ? intl.formatMessage(
            {id: 'leads.list.inQueueWith', defaultMessage: 'In queue - {owner}'},
            {owner: normalizedOwner}
          )
        : intl.formatMessage({id: 'leads.list.inQueue', defaultMessage: 'In queue'})
    },
    [intl]
  )

  const buildSelectedLead = useCallback(
    (leadNode: AllLeadStageEdge['node']) => {
      const assignedUser = leadNode.user?.edges?.[0]?.node
      return {
        id: leadNode.id,
        name: leadNode.name,
        stage: stageName,
        user: assignedUser
          ? `${assignedUser.firstName ?? ''} ${assignedUser.lastName ?? ''}`.trim()
          : '',
      }
    },
    [stageName]
  )

  const toggleLeadSelection = useCallback(
    (leadNode: AllLeadStageEdge['node']) => {
      const exists = selectedLeads.some((item) => item.id === leadNode.id)
      if (exists) {
        setLeadsSelected(selectedLeads.filter((item) => item.id !== leadNode.id))
        return
      }
      setLeadsSelected([...selectedLeads, buildSelectedLead(leadNode)])
    },
    [buildSelectedLead, selectedLeads, setLeadsSelected]
  )

  const toggleStageSelection = useCallback(() => {
    const selectableLeadIds = new Set(selectableLeadEdges.map(({node}) => node.id))
    if (isAllSelected) {
      setLeadsSelected(selectedLeads.filter((lead) => !selectableLeadIds.has(lead.id)))
      return
    }

    const nextSelected = [...selectedLeads]
    selectableLeadEdges.forEach(({node}) => {
      if (!nextSelected.some((item) => item.id === node.id)) {
        nextSelected.push(buildSelectedLead(node))
      }
    })
    setLeadsSelected(nextSelected)
  }, [buildSelectedLead, isAllSelected, selectableLeadEdges, selectedLeads, setLeadsSelected])

  useEffect(() => {
    if (!localShouldLoad) return
    requestLeads(offset)
  }, [
    localShouldLoad,
    stageId,
    offset,
    datesFrom,
    datesTo,
    searchName,
    searchPhone,
    valueUsers,
    requestLeads,
  ])

  // useEffect(() => {
  //   if (error) toast.error(`Error: ${error.message}`)
  // }, [error])
  useEffect(() => {
    if (errorTotal)
      toast.error(
        intl.formatMessage(
          {id: 'common.error.withMessage', defaultMessage: 'Error: {message}'},
          {message: errorTotal.message}
        )
      )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errorTotal])

  useEffect(() => {
    if (!leadsSelected?.length || !leads.length) return

    const lockedVisibleLeadIds = new Set(
      leads.filter(({node}) => node.dialQueueLocked).map(({node}) => node.id)
    )
    if (!lockedVisibleLeadIds.size) return

    const nextSelected = leadsSelected.filter((lead) => !lockedVisibleLeadIds.has(lead.id))
    if (nextSelected.length !== leadsSelected.length) {
      setLeadsSelected(nextSelected)
    }
  }, [leads, leadsSelected, setLeadsSelected])

  useEffect(() => {
    if (!data) return
    if (isNaN(data.totalLeads)) return
    initTotal({
      idStage: stageId,
      total: Number(data.totalLeads),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  useEffect(() => {
    if (!data) return
    const {edges} = data.allLeads
    if (edges.length === 0) return
    updateOneLead({idStage: stageId, leads: edges})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  if (isLoadingBoard || loading) {
    return (
      <Card.Body
        className='placeholder-glow p-0 mb-1'
        style={{overflowY: 'auto', minHeight: 'calc(100vh - 200px)'}}
      >
        <div style={{borderBottom: '1px solid #f1f1f2'}} className='px-3 py-2 pb-2 mb-4'>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <div>
              <div className='d-flex align-items-center gap-3'>
                <button className={`m-0 p-0 border-0 bg-transparent`} disabled>
                  <i className='bi bi-arrow-left' style={{color: 'inherit'}} />
                </button>
                <span className='text-pagination'>
                  {offset / FIRST + 1}
                  {slash}
                  {total ? (
                    Math.ceil(total / FIRST)
                  ) : total == null ? (
                    <Spinner animation='grow' size='sm' />
                  ) : (
                    total
                  )}
                </span>
                <button className={`m-0 p-0 border-0 bg-transparent`} disabled>
                  <i className='bi bi-arrow-right' />
                </button>
              </div>
            </div>
          </div>
        </div>
        {[1, 2, 3, 4].map((ff) => (
          <Card
            key={ff}
            className='placeholder'
            style={{
              padding: kz(8 * 2),
              margin: `0 0 ${kz(8)} 0`,
              background: '#c4c4c4',
              height: kz(80 + (Math.floor(Math.random() * 10) % 2 === 0 ? 20 : 0)),
              display: 'block',
            }}
          />
        ))}
      </Card.Body>
    )
  }

  const hasNextPage =
    localShouldLoad && data
      ? data.allLeads.pageInfo.hasNextPage
      : total != null && offset + FIRST < total

  if (leads.length === 0) {
    return (
      <Card.Body className='p-0 mb-1' style={{maxHeight: '100%', overflowY: 'auto'}}>
        <Card.Body>
          <Card.Text>
            <FormattedMessage
              id='leads.list.stageEmpty'
              defaultMessage='This stage does not contain any lead assigned'
            />
          </Card.Text>
          <div className='d-flex align-items-center justify-content-center w-100'>
            <div style={{maxWidth: '200px'}}>
              <Lottie animationData={noLeads} autoPlay style={{maxWidth: '200px'}} />
            </div>
          </div>
          {providedParent.placeholder}
        </Card.Body>
      </Card.Body>
    )
  }

  return (
    <>
      <Card.Body className='p-0 mb-1' style={{minHeight: 'calc(100vh - 200px)', overflowY: 'auto'}}>
        <div style={{borderBottom: '1px solid #f1f1f2'}} className='px-3 py-2 pb-2 mb-4'>
          <div className='d-flex align-items-center justify-content-between w-100'>
            <div className='d-flex align-items-center'>
              <input
                className='form-check-input mt-0'
                type='checkbox'
                style={{width: kz(16), height: kz(16)}}
                checked={isAllSelected}
                onChange={toggleStageSelection}
                disabled={!selectableLeadEdges.length}
                aria-label={intl.formatMessage(
                  {
                    id: 'leads.board.selectLeadsInStage',
                    defaultMessage: 'Select leads in {stage}',
                  },
                  {stage: stageName}
                )}
              />
            </div>
            <div>
              <div className='d-flex align-items-center gap-3'>
                <button
                  className={`m-0 p-0 border-0 bg-transparent ${offset > 0 ? 'text-primary' : ''}`}
                  disabled={offset === 0}
                  onClick={() => {
                    setLocalShouldLoad(true)
                    setOffset((p) => p - FIRST)
                  }}
                >
                  <i className='bi bi-arrow-left' />
                </button>
                <span className='text-pagination'>
                  {offset / FIRST + 1}
                  {slash}
                  {total ? (
                    Math.ceil(total / FIRST)
                  ) : total == null ? (
                    <Spinner animation='grow' size='sm' />
                  ) : (
                    total
                  )}
                </span>
                <button
                  className={`m-0 p-0 border-0 bg-transparent ${hasNextPage ? 'text-primary' : ''}`}
                  disabled={!hasNextPage}
                  onClick={() => {
                    setLocalShouldLoad(true)
                    setOffset((p) => p + FIRST)
                  }}
                >
                  <i className='bi bi-arrow-right' />
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className='cards-scrollable'>
          {leads.map(({node}, idx) => (
            <Draggable key={node.id} draggableId={`${parseId(node.id)}`} index={idx}>
              {(provided, snapshot) => {
                const isQueueLocked = Boolean(node.dialQueueLocked)
                const queueLockLabel = buildDialQueueLockLabel(node.dialQueueOwner)
                const isSelected = selectedLeads.some((item) => item.id === node.id)

                return (
                  <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    style={{
                      ...getItemStyle(snapshot.isDragging, provided.draggableProps.style),
                      borderColor: isSelected ? '#0d6efd' : undefined,
                      boxShadow: isSelected ? '0 0 0 1px rgba(13, 110, 253, 0.15)' : undefined,
                    }}
                    onClick={() => {
                      setInfoLead({name: node.name, id: node.id})
                      onOpenEdit()
                    }}
                  >
                    <div className='d-flex justify-content-between'>
                      <div className='d-flex flex-column w-100'>
                        <div className='ms-2 c-details'>
                          <div className='d-flex align-items-start justify-content-between gap-2'>
                            <h6 className='mb-0 text-capitalize d-flex align-items-center gap-1' style={{maxWidth: '17ch'}}>
                              {node.name}
                              <LeadSourceBadge name={node.leadSource?.name} className='fs-9' />
                            </h6>
                            <input
                              className='form-check-input flex-shrink-0 mt-1'
                              type='checkbox'
                              style={{width: kz(15), height: kz(15)}}
                              checked={isSelected}
                              disabled={isQueueLocked}
                              title={isQueueLocked ? queueLockLabel : undefined}
                              aria-label={intl.formatMessage(
                                {id: 'leads.board.selectLead', defaultMessage: 'Select {name}'},
                                {name: node.name}
                              )}
                              onClick={(e) => {
                                e.stopPropagation()
                              }}
                              onChange={(e) => {
                                e.stopPropagation()
                                if (isQueueLocked) return
                                toggleLeadSelection(node)
                              }}
                            />
                          </div>
                          <small style={{display: 'block'}}>
                            <PhoneProviderActions phone={node.phone} />
                          </small>
                          <div>
                            <span style={{fontSize: kz(10)}}>
                              <FormattedMessage
                                id='leads.list.card.enteredOn'
                                defaultMessage='Entered on'
                              />
                              {colon}
                            </span>
                            {space}
                            <span style={{fontSize: kz(10)}}>
                              {moment(node.created).format('DD MMM YYYY HH:mm')}
                            </span>
                          </div>
                        </div>
                        {node.user.edges.length > 0 && (
                          <div className='d-flex justify-content-end'>
                            <div
                              style={{
                                padding: `${kz(3)} ${kz(2)}`,
                                height: '50x',
                                backgroundColor: 'transparent',
                                borderRadius: '5px',
                                border: `1px solid ${
                                  node.tagInsurance?.edges?.[0]?.node?.color || 'gray'
                                }`,
                                color: node.tagInsurance?.edges?.[0]?.node?.color || 'gray',
                                overflow: 'hidden',
                                maxWidth: kz(130),
                                fontSize: kz(10),
                                whiteSpace: 'nowrap',
                                textOverflow: 'ellipsis',
                                wordSpacing: '1',
                              }}
                              data-tooltip-id={`${
                                node.user.edges[0].node.firstName
                              }-${idx}-${parseId(node.id)}`}
                              data-tooltip-content={`${
                                node.personalLead
                                  ? intl.formatMessage({
                                      id: 'leads.list.card.personal',
                                      defaultMessage: '[Personal]',
                                    })
                                  : node.comercialLead
                                  ? intl.formatMessage({
                                      id: 'leads.list.card.commercial',
                                      defaultMessage: '[Commercial]',
                                    })
                                  : dash
                              } - ${node.user.edges[0].node.firstName} ${
                                node.user.edges[0].node.lastName
                              }`}
                            >
                              {toTitleCase(node.user.edges[0].node.firstName)}
                              {space}
                              {node.user.edges[0].node.lastName?.[0]?.toUpperCase()}
                              {initialSep}
                              {space}
                              {node.user.edges[0].node.email?.split('@')[0]}
                            </div>
                            <Tooltip
                              id={`${node.user.edges[0].node.firstName}-${idx}-${parseId(node.id)}`}
                              place='right'
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    <AssignmentGroupBadge
                      group={node.assignmentGroup}
                      withinWindow={node.withinGroupExclusivity}
                      className='mt-2'
                    />
                    {stageType === 'SOLD' || stageType === 'DECLINED' ? (
                      <hr style={{color: '#7c8392'}} />
                    ) : (
                      <div
                        className={getClassTraffic(intl, new Date(node.created)).class}
                        title={getClassTraffic(intl, new Date(node.created)).title}
                      ></div>
                    )}

                    <div className='d-flex align-items-md-center align-items-start justify-content-between gap-2 flex-column flex-md-row'>
                      <div className='d-flex flex-column gap-2 w-100'>
                        <div className='d-flex align-items-center gap-2' style={{flexWrap: 'wrap'}}>
                          {isQueueLocked && (
                            <Badge bg='warning' text='dark' className='border'>
                              {queueLockLabel}
                            </Badge>
                          )}
                          {node.tagInsurance.edges.map(({node: nodeTag}) => (
                            <div
                              style={{
                                padding: `${kz(3)} ${kz(5)}`,
                                borderRadius: '30px',
                                backgroundColor: nodeTag.color,
                                color: 'white',
                                fontWeight: 'bolder',
                                fontSize: getTagFontSize(nodeTag.name),
                                lineHeight: 1.2,
                              }}
                              key={nodeTag.id}
                            >
                              {nodeTag.name}
                            </div>
                          ))}
                        </div>
                        {node.appointmentSet?.edges?.[0]?.node && (
                          <div
                            className='d-flex align-items-center gap-2'
                            style={{
                              fontSize: kz(11),
                              padding: `${kz(4)} ${kz(8)}`,
                              backgroundColor: '#e7f3ff',
                              borderRadius: '4px',
                              border: '1px solid #0d6efd',
                            }}
                          >
                            <i className='bi bi-calendar-event text-primary' />
                            <span className='fw-bold text-primary'>
                              {(() => {
                                const appointmentDate = node.appointmentSet.edges[0].node.date
                                const hasTime =
                                  appointmentDate?.includes('T') || appointmentDate?.includes(' ')
                                return hasTime
                                  ? moment.utc(appointmentDate).local().format('MMM DD, YYYY HH:mm')
                                  : moment(appointmentDate).format('MMM DD, YYYY')
                              })()}
                            </span>
                            <span className='text-muted'>
                              {node.appointmentSet.edges[0].node.tittle}
                            </span>
                          </div>
                        )}
                      </div>
                      {declinedStage && stageType !== 'DECLINED' && (
                        <Button
                          onClick={(e) => {
                            e.stopPropagation()
                            setInfoLeadDead({
                              idDeadLead: node.id,
                              idNameLead: node.name,
                            })
                            onOpenDeadLead()
                          }}
                          className='fs-2 btn btn-danger btn-very-smaller btn-delete-leads'
                          variant='secondary'
                          type='button'
                          id='button-addon1'
                        >
                          <i className='bi bi-trash' />
                        </Button>
                      )}
                      <button
                        style={{
                          flex: 0,
                          backgroundColor: 'transparent',
                          border: 0,
                          padding: 0,
                          margin: 0,
                        }}
                        onClick={(e) => {
                          e.stopPropagation()
                          onOpenCall()
                          setIdLead(node.id)
                          setPhone(node.phone)
                        }}
                      >
                        <Badge bg='success' pill className='fs-2 d-flex align-items-center gap-1'>
                          <i className='bi bi-telephone-plus' style={{color: 'inherit'}} />
                          {node.callCounter <= 10 ? node.callCounter : tenPlus}
                        </Badge>
                      </button>
                    </div>
                    <div className='cards last-update '>
                      <span style={{fontSize: kz(10)}}>
                        <FormattedMessage
                          id='leads.list.card.lastUpdate'
                          defaultMessage='Last update'
                        />
                        {colon}
                      </span>
                      {space}
                      <span style={{fontSize: kz(10)}}>
                        {moment(node.updated).format('DD MMM YYYY HH:mm')}
                      </span>
                    </div>
                  </Card>
                )
              }}
            </Draggable>
          ))}
          {leads.length === 0 && (
            <Draggable
              draggableId='none-23231'
              index={-1}
              isDragDisabled
              disableInteractiveElementBlocking
            >
              {(provided, snapshot) => (
                <div
                  ref={provided.innerRef}
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                >
                  <Card.Text>
                    <FormattedMessage
                      id='leads.list.stageEmpty'
                      defaultMessage='This stage does not contain any lead assigned'
                    />
                  </Card.Text>
                  <div className='d-flex align-items-center justify-content-center w-100'>
                    <div style={{maxWidth: '200px'}}>
                      <Lottie animationData={noLeads} autoPlay style={{maxWidth: '200px'}} />
                    </div>
                  </div>
                </div>
              )}
            </Draggable>
          )}
          {providedParent.placeholder}
        </div>
      </Card.Body>

      {idLead && (
        <ModalSumCall
          id={idLead}
          isOpen={isOpenCall}
          onClose={onCloseCall}
          refetch={refreshStage}
          isRingIntegration={isRingIntegration}
          phone={phone}
        />
      )}
      {isOpenDeadLead && (
        <ModalDeadLead
          isOpen={isOpenDeadLead}
          refetch={refreshStage}
          onClose={() => {
            setInfoLeadDead({
              idDeadLead: '',
              idNameLead: '',
            })
            onCloseDeadLead()
          }}
          info={{id: infoLeadDead.idDeadLead, name: infoLeadDead.idNameLead}}
        />
      )}
    </>
  )
}
